import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, Output, PLATFORM_ID } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { isPlatformBrowser } from '@angular/common';
import { CustomIcons } from '@shared/models/custom-icons';
import { finalize, take, takeUntil, tap } from 'rxjs/operators';
import { AuthState } from '@auth/store/auth/auth.state';
import { Store } from '@ngxs/store';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SharedState } from '@shared/state/shared/shared.state';
import { ActivatedRoute, Router } from '@angular/router';
import { MirSnackbarType } from '@shared/components/snackbar-message/model/snackbar';
import { SnackBarService } from '@shared/components/snackbar-message/services/snackbar.service';
import { AppealsService } from '@shared/components/appeals/services/appeals.service';
import { whitespaceValidator } from '@utils/form-validators';
import { SharedCatalogState } from '@shared/state/catalog/shared-catalog.state';
import { StatsMosService } from '@shared/services/stats-mos.service';
import { Subject } from 'rxjs';
import { TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { SuccessModalDialogComponent } from '../success-modal-dialog/success-modal-dialog.component';
import { recoveryFormValue } from '../../utils';
import { CatalogItemsState } from '../../../../../data/modules/catalog-items-data/store/catalog-items-state';
import { ImpairedCatalogItemsRoutes } from '../../../../../version-for-visually-impaired/modules/data/modules/models/routes';
import { CatalogItemsDataRouting } from '../../../../../data/modules/catalog-items-data/models/catalog-items-data-routing';
import { VersionForVisuallyImpairedRoutes } from '../../../../../version-for-visually-impaired/models/routes';

@Component({
  selector: 'app-contact-information',
  templateUrl: './contact-information.component.html',
  styleUrls: ['./contact-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class ContactInformationComponent implements OnDestroy {
  private _sessionStorageKey = 'ContactObjectForm';
  private _isBackForm = false;
  private _isMobile = false;
  public cancelName = 'OTHER.CANCEL';
  public cancelNameTooltip = 'OTHER.TO_DATASETS';
  public step: number;
  public componentDestroy: () => Subject<any>;
  @Output() public readonly sendSuccessfully = new EventEmitter<number>();
  @Output() public readonly goStep1 = new EventEmitter();

  @Input() public title: string;
  @Input() public isImpaired: boolean;

  @Input() public set setDefaultValue(step: number) {
    if (step === 2) {
      this.step = 2;
      this.cancelName = 'OTHER.CANCEL';
      this.cancelNameTooltip = '';
      this._isBackForm = false;
      const user = this.store.selectSnapshot(AuthState.authenticatedUser);
      if (user) {
        const fio = user.fio.split(' ');
        if (fio[0] && !this.form.get('surname')?.value) {
          this.form.get('surname')?.setValue(fio[0]);
        }

        if (fio[1] && !this.form.get('name')?.value) {
          this.form.get('name')?.setValue(fio[1]);
        }

        if (fio[2] && !this.form.get('secondName')?.value) {
          this.form.get('secondName')?.setValue(fio[2]);
        }

        const userProfile = this.store.selectSnapshot(SharedState.userDataProfiles);

        if (userProfile?.contactEmail && userProfile.contactEmail !== 'OTHER.NOT_DATA' && !this.form.get('email')?.value) {
          this.form.get('email')?.setValue(userProfile.contactEmail);
        }
      }

      recoveryFormValue(this.platformId, this._sessionStorageKey, this.form, ['captcha']);
    }
  }

  @Input() public firstStepFrom: UntypedFormGroup;
  public loader = false;
  public form: UntypedFormGroup = new UntypedFormGroup({
    surname: new UntypedFormControl('', [Validators.required, whitespaceValidator]),
    name: new UntypedFormControl('', [Validators.required, whitespaceValidator]),
    secondName: new UntypedFormControl(''),
    email: new UntypedFormControl('', [
      Validators.required,
      whitespaceValidator,
      Validators.pattern(/^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/)
    ]),
    telephone: new UntypedFormControl('', [Validators.minLength(11), whitespaceValidator]),
    captcha: new UntypedFormControl('', [Validators.required, Validators.requiredTrue])
  });

  public icons = CustomIcons;
  public breakPointObserve$ = this.breakpointObserver.observe(['(max-width: 420px)']).pipe(
    tap(state => {
      this._isMobile = state.breakpoints['(max-width: 420px)'];
    })
  );
  constructor(
    public dialog: MatDialog,
    private appealService: AppealsService,
    private store: Store,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: string,
    private breakpointObserver: BreakpointObserver,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private snackBar: SnackBarService,
    private statsMosService: StatsMosService
  ) {}

  private resetForm(): void {
    this.form.get('captcha')?.reset();
    this.appealService.resetMap.next(true);
    const formTypeControl = this.firstStepFrom.get('formType');
    const nameCatalogControl = this.firstStepFrom.get('nameCatalog');
    const themeIdControl = this.firstStepFrom.get('themeId');
    const rowNumberControl = this.firstStepFrom.get('rowNumber');
    let rowNumber = 0;
    if (rowNumberControl) {
      rowNumber = rowNumberControl?.value;
    }
    const themeId = themeIdControl?.value;
    const formType = formTypeControl?.value;
    const nameCatalog = nameCatalogControl?.value;

    this.firstStepFrom.reset('', {
      emitEvent: true
    });
    formTypeControl?.setValue(formType, {
      emitEvent: false
    });

    if (formType === 'new') {
      themeIdControl?.setValue(themeId, {
        emitEvent: false
      });
    }

    nameCatalogControl?.setValue(nameCatalog, {
      emitEvent: false
    });

    if (rowNumberControl) {
      rowNumberControl?.setValue(rowNumber, {
        emitEvent: false
      });
    }
  }

  public sendForm(): void {
    if (!this.firstStepFrom.valid) {
      this.snackBar.open(MirSnackbarType.Error, [{ message: 'SNACK_BAR_MESSAGE.APPEAL_ERROR_STEP_1', translate: true }]);
      return;
    }

    this.loader = true;

    this.cdr.detectChanges();
    const catalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
    this.statsMosService
      .fireEvent({ action: 'reportsoesg', idDataset: catalog?.datasetId })
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        error: err => {
          if (err.status !== 200) {
            throw err;
          }
        }
      });
    this.appealService
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      .formSubmission(this.firstStepFrom.value.formType, { ...this.form.value, ...this.firstStepFrom.value })
      .pipe(
        take(1),
        finalize(() => {
          this.loader = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.code === 200) {
          this.sendSuccessfully.emit(+res.data.id);
          const config = this._isMobile
            ? {
                maxWidth: '100%',
                width: '100%',
                position: {
                  bottom: '0px'
                },
                panelClass: 'mobile-bottom-show-up-small-dialog',
                backdropClass: 'backdrop',
                hasBackdrop: true
              }
            : { backdropClass: 'closer-backdrop', panelClass: this.isImpaired ? 'cdk-version-for-visually-impaired' : '' };
          const dialogRef = this.dialog.open(SuccessModalDialogComponent, config);
          dialogRef.componentInstance.title = this.title;
          dialogRef.componentInstance.appealId = res.data.id;
          dialogRef.componentInstance.isImpaired = this.isImpaired;
          if (isPlatformBrowser(this.platformId)) {
            if (this.firstStepFrom.value.formType === 'new') {
              sessionStorage.removeItem('NewObjectForm');
            } else {
              sessionStorage.removeItem('IncorrectObjectForm');
            }
          }

          this.cancelName = 'OTHER.BACK';
          this.cancelNameTooltip = 'OTHER.BACK';
          this._isBackForm = true;
          this.resetForm();
        }
      });
  }

  public cancel(): void {
    if (this._isBackForm) {
      this.goStep1.emit();
      return;
    }

    const params = this.activeRoute.snapshot.queryParams;
    const catalogItemGlobalId = params?.catalogItemGlobalId;
    const isBackHome = params?.isBackHome;
    const catalogGlobalId = params?.catalogGlobalId;
    const selectCatalog = this.store.selectSnapshot(SharedCatalogState.selectCatalog);
    let backUrl = `opendata/${selectCatalog.datasetId}/${CatalogItemsDataRouting.CatalogItems}`;
    if (this.isImpaired) {
      backUrl = `visually-impaired/${VersionForVisuallyImpairedRoutes.DataSet}/${catalogGlobalId}/${ImpairedCatalogItemsRoutes.Table}`;
    }

    let queryParams: any = {};

    if (catalogItemGlobalId) {
      queryParams = { objectId: catalogItemGlobalId };
    }

    const currentCatalog = this.store.selectSnapshot(CatalogItemsState.getCatalogItem).call(this, +catalogGlobalId);
    if (isBackHome && isBackHome === 'true') {
      backUrl = '/';
      queryParams = {
        catalogItemGlobalId: catalogItemGlobalId,
        catalogGlobalId: catalogGlobalId
      };
    }

    if (currentCatalog && currentCatalog.isDynamic !== undefined) {
      queryParams = {
        isDynamic: currentCatalog.isDynamic
      };
    }

    if (currentCatalog && currentCatalog.pageSize && (currentCatalog.pageIndex || currentCatalog.pageIndex === 0)) {
      queryParams = {
        ...queryParams,
        pageSize: currentCatalog.pageSize,
        pageIndex: currentCatalog.pageIndex
      };
    }

    this.router.navigate([backUrl], {
      queryParams: queryParams
    });
  }

  public ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem(this._sessionStorageKey, JSON.stringify(this.form.value));
    }
  }

  public enterEvent($event: any): void {
    $event.preventDefault();
    $event.stopPropagation();
  }
}
